import { Box, Typography } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState, useMemo } from "react";
import {
  CircularLoading,
  LargeContentBox,
} from "../../../../../../components/common";
import { oids } from "../../../../../../configs/Settings";
import { useSessionContext } from "../../../../../../contexts/SessionContext";
import { usePractitionerRoleContext } from "../../../../../../contexts/PractitionerRoleContext";
import { useSchedule } from "../../../../../../services/fhir/schedule/hooks/useSchedule";
import { DateFilterType } from "../../../../../../utils/interfaces";
import { NoScheduleOptions } from "../../../../NoScheduleOptions";
import { ProfessionalSchedule } from "@/components/schedule/ProfessionalSchedule";
import { useViewContext } from "@/contexts/ViewContext";
import {
  practitionerRefsSharingAssociationCnpj,
  scheduleTouchesAllowedPractitioners,
} from "@/utils/practitionerRoleAssociationScope";

export function PractitionerDashboard() {
  const { user, access } = useSessionContext();
  const { viewType } = useViewContext();
  const { practitionerRole } = usePractitionerRoleContext();

  const [refetch, setRefetch] = useState<boolean>(false);
  const [dateFilter, setDateFilter] = useState<Dayjs>(dayjs());
  const [filterType, setFilterType] = useState<DateFilterType>("day");

  // Hook para buscar agendamentos do profissional
  const {
    data: scheduleData,
    loading: scheduleLoading,
    error: scheduleError,
    fetchSchedules,
  } = useSchedule({
    actor:
      viewType === "user"
        ? `Practitioner/${oids.cpf}-${user.username}`
        : undefined,
    organization: `${oids.cnpj}-${access.organizationId}`,
    active: true,
  });

  const scopedSchedules = useMemo(() => {
    const list = scheduleData?.schedules;
    if (!list?.length) return list;
    const scopeDigits = access?.managerAssociationScopeCnpjDigits;
    const allowed = practitionerRefsSharingAssociationCnpj(
      practitionerRole,
      scopeDigits,
    );
    if (viewType !== "admin") return list;
    // Escopo ativo mas ainda sem roles no contexto: não exibir agendas de outros CNPJs.
    if (scopeDigits) {
      if (allowed.size === 0) return [];
      return list.filter((s) => scheduleTouchesAllowedPractitioners(s, allowed));
    }
    return list;
  }, [
    scheduleData?.schedules,
    access?.managerAssociationScopeCnpjDigits,
    practitionerRole,
    viewType,
  ]);

  // Busca agendamentos quando o componente monta
  useEffect(() => {
    if (access?.organizationId && user.username) {
      fetchSchedules();
    }
  }, [
    access?.organizationId,
    access?.managerAssociationScopeCnpjDigits,
    user.username,
    fetchSchedules,
    viewType,
  ]);

  // Exibe loading enquanto carrega os agendamentos
  if (scheduleLoading) {
    return (
      <LargeContentBox>
        <CircularLoading />
      </LargeContentBox>
    );
  }

  // Exibe erro se houver
  if (scheduleError) {
    console.error("Erro ao carregar agendamentos:", scheduleError);
    return (
      <LargeContentBox>
        <Typography color="error">
          Erro ao carregar agendamentos. Por favor, tente novamente.
        </Typography>
      </LargeContentBox>
    );
  }

  return (
    <>
      <Box
        sx={{
          width: "100%",
          backgroundColor: "neutral0.main",
          borderRadius: "20px",
        }}
      >
        {!scopedSchedules?.length ? (
          <NoScheduleOptions />
        ) : (
          <ProfessionalSchedule
            schedules={scopedSchedules}
            dateFilter={dateFilter}
            filterType={filterType}
            setDateFilter={setDateFilter}
            setFilterType={setFilterType}
            setRefetch={setRefetch}
            refetch={refetch}
          />
        )}
      </Box>
    </>
  );
}
